import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileSpreadsheet, FileText, Mail, Plus, ScanLine } from 'lucide-react';
import { formatAttendanceBreakdown } from '@peles/shared';
import type { InviteCampaign } from '@peles/shared';
import { EntityList, StatusBadge } from '../common';
import type { EntityListColumn } from '../common';
import { useDialog } from '../../context/DialogContext';
import {
  useInviteCampaigns,
  useDeleteInviteCampaign,
  useCreateInviteCampaign,
} from '../../hooks/useInvites';
import { downloadExport, triggerDownload, type ExportFormat } from '../../api/export';
import { campaignHref, campaignStatusBadge } from './campaign/campaignLinks';
import { MailLogView } from '../messaging/MailLogView';
import { MessagesSection } from './MessagesSection';

interface InviteRsvpSectionProps {
  eventId: string;
  /**
   * Client-portal mode (a client with canManageInvites): campaigns are made
   * from the event's own designs, only a draft never sent can be deleted,
   * broadcasts go by email, and the office's check-in page is not linked.
   */
  clientMode?: boolean;
}

/**
 * Embedded "Invites & RSVP" section for the event detail page: the event's
 * campaigns, each opening on its own page (`CampaignWorkspace`), and the
 * guest broadcasts and message history beside them.
 */
export function InviteRsvpSection({ eventId, clientMode = false }: InviteRsvpSectionProps) {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const { confirm, alert } = useDialog();
  const { data: campaigns = [], isLoading } = useInviteCampaigns(eventId);
  const deleteCampaign = useDeleteInviteCampaign();
  const createCampaign = useCreateInviteCampaign();
  const navigate = useNavigate();

  /**
   * Guest-by-guest RSVP report — every guest with their answer, head counts
   * and the state of their invitation. PDF to work from, CSV to hand to the
   * caterer or open in a spreadsheet.
   */
  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    try {
      const blob = await downloadExport(eventId, 'rsvp-report', format);
      triggerDownload(blob, `rsvp-report.${format}`);
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to build the RSVP report',
        variant: 'error',
      });
    } finally {
      setExporting(null);
    }
  };

  const openCampaign = (campaign: InviteCampaign) =>
    navigate(campaignHref(eventId, campaign.id, clientMode));

  /** A new campaign starts as a draft and opens on its first step. */
  const openCreate = async () => {
    try {
      const created = await createCampaign.mutateAsync({
        eventId,
        name: campaigns.length ? `Invitation ${campaigns.length + 1}` : 'Invitation',
      });
      navigate(
        `${campaignHref(eventId, created.id, clientMode)}${clientMode ? '&' : '?'}step=details`,
      );
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not start a new invitation',
        variant: 'error',
      });
    }
  };

  const handleDelete = async (campaign: InviteCampaign) => {
    const ok = await confirm({
      message: `Delete campaign "${campaign.name}"? Its recipients and RSVP links will stop working.`,
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteCampaign.mutateAsync(campaign.id);
  };

  const columns: EntityListColumn<InviteCampaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      render: (c) => (
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900">{c.name}</span>
        </div>
      ),
      sortable: true,
      sortValue: (c) => c.name,
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => {
        const badge = campaignStatusBadge(c);
        return <StatusBadge label={badge.label} color={badge.color} />;
      },
    },
    {
      key: 'recipients',
      header: 'Recipients',
      align: 'center',
      hideOnMobile: true,
      render: (c) => <span>{c.recipientCount ?? 0}</span>,
    },
    {
      key: 'responded',
      header: 'Responded',
      align: 'center',
      hideOnMobile: true,
      render: (c) => <span>{c.respondedCount ?? 0}</span>,
    },
    {
      key: 'attendants',
      header: 'Attendants',
      align: 'center',
      render: (c) =>
        c.attendingCount ? (
          <div>
            <div className="font-medium text-gray-900">{c.attendingCount}</div>
            <div className="text-xs text-gray-500">
              {formatAttendanceBreakdown(c.attendingAdults ?? 0, c.attendingKids ?? 0)}
            </div>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'deadline',
      header: 'RSVP By',
      hideOnMobile: true,
      render: (c) =>
        c.rsvpDeadline ? (
          <span>
            {new Date(c.rsvpDeadline).toLocaleDateString('en-US', { dateStyle: 'medium' })}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'by',
      header: 'By',
      hideOnMobile: true,
      render: (c) => (
        <span className="text-xs text-gray-600">{c.sentByName ?? c.createdByName ?? '—'}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      // A client may throw away a draft; a campaign that went out holds guests' links.
      render: (c) =>
        clientMode && (c.status !== 'DRAFT' || c.sentAt) ? null : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleDelete(c);
            }}
            className="text-sm text-gray-400 hover:text-red-600"
          >
            Delete
          </button>
        ),
    },
  ];

  return (
    <div className="space-y-3" data-testid="invite-rsvp-section">
      <div className="flex flex-wrap justify-end gap-2">
        {/* The report is the answer to "who hasn't replied yet" — available to
            clients too, since they see this section only when permitted. */}
        <button
          type="button"
          onClick={() => void handleExport('pdf')}
          disabled={exporting !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <FileText className="w-4 h-4" />
          {exporting === 'pdf' ? 'Preparing…' : 'RSVP Report'}
        </button>
        <button
          type="button"
          onClick={() => void handleExport('csv')}
          disabled={exporting !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <FileSpreadsheet className="w-4 h-4" />
          {exporting === 'csv' ? 'Preparing…' : 'Export CSV'}
        </button>
        {!clientMode && (
          <Link
            to={`/events/${eventId}/checkin`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ScanLine className="w-4 h-4" />
            Check-In
          </Link>
        )}
        <button
          type="button"
          onClick={() => void openCreate()}
          disabled={createCampaign.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          New invitation
        </button>
      </div>

      {!isLoading && campaigns.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">
          No invitations yet. Start one to send invitations and collect RSVPs.
        </div>
      ) : (
        <EntityList
          items={campaigns}
          columns={columns}
          getKey={(c) => c.id}
          isLoading={isLoading}
          onRowClick={openCampaign}
        />
      )}

      <MessagesSection eventId={eventId} clientMode={clientMode} />

      {/* Everything actually delivered to guests — the answer to "did they
          get it?", including the message body. */}
      <details className="border border-gray-200 rounded-lg">
        <summary className="px-4 py-3 text-sm font-semibold text-gray-900 cursor-pointer">
          Message history
        </summary>
        <div className="p-4 pt-0">
          <MailLogView eventId={eventId} />
        </div>
      </details>
    </div>
  );
}
